import React, {useState, useEffect, useCallback} from "react";
import {useLocation, useNavigate} from "react-router-dom";

import {getAccessToken} from "../utils/tokenManager";
import {getBoundFetch} from "../utils/getBoundFetch";
import {trackNeuronEvent} from "../utils/trackNeuronEvent";
import {NEURON_EVENTS} from "../config/eventTypes";
import SectionHeading from "./SectionHeading";
import {Badge} from "./ui/badge";
import {Skeleton} from "./ui/skeleton";

const SEARCH_URL = "https://api.neuronsearchlab.com/v1/search";

/**
 * Search, and why it matters to the ranking.
 *
 * A query is the clearest statement of intent a reader ever gives you, so this
 * page does two things: it runs the query through NSL's semantic search, and
 * when a result is opened it records a `Search result opened` event carrying
 * the query and the search's request_id. That event feeds the same affinity the
 * personalised rails read from, so searching for "flags" shapes the front page
 * on the next visit - search is not a dead end that only serves this screen.
 *
 * The previous implementation called a legacy endpoint with a hardcoded API key
 * and recorded nothing at all.
 */
const SearchPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState([]);
  const [requestId, setRequestId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const userEmail =
    (typeof localStorage !== "undefined" &&
      localStorage.getItem("userEmail")) ||
    "";

  const runSearch = useCallback(
    async (query) => {
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessToken();
        const fetchImpl = getBoundFetch() || fetch;
        const params = new URLSearchParams({q: query, limit: "24"});
        if (userEmail) params.set("user_id", userEmail);
        // Articles only: comments outnumber articles in the catalogue and would
        // otherwise dominate the result list.
        params.append("filter", "type!=comment");

        const response = await fetchImpl(`${SEARCH_URL}?${params.toString()}`, {
          method: "GET",
          headers: {Accept: "application/json", Authorization: `Bearer ${token}`},
        });
        if (!response.ok) throw new Error(`Search failed (${response.status})`);

        const payload = await response.json();
        setRequestId(payload?.request_id || null);
        setResults(
          (payload?.data || []).map((row) => ({
            id: row.entity_id,
            title: row.metadata?.originalTitle || row.name || "Untitled",
            summary: row.metadata?.summary || row.description || "",
            image: row.metadata?.image || "",
            category:
              row.metadata?.aiCategorization || row.metadata?.topic || "",
            source: row.metadata?.source || "",
            score: typeof row.score === "number" ? row.score : null,
            metadata: row.metadata,
          }))
        );
      } catch (err) {
        console.error("[search] failed:", err);
        setError(err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [userEmail]
  );

  useEffect(() => {
    const query = new URLSearchParams(location.search).get("q") || "";
    setSearchQuery(query);
    if (!query.trim()) {
      setResults([]);
      return;
    }
    runSearch(query);
  }, [location.search, runSearch]);

  const openResult = async (result) => {
    // Record the intent before navigating. Failure here must never block the
    // reader, so it is fire-and-forget.
    if (userEmail) {
      trackNeuronEvent({
        eventId: NEURON_EVENTS.searchResultOpened,
        itemId: result.id,
        userId: userEmail,
        requestId: requestId || undefined,
        metadata: {surface: "search", query: searchQuery},
      }).catch((err) => console.warn("[search] event not recorded:", err));
    }

    try {
      sessionStorage.setItem(
        `neuron:article:${result.id}`,
        JSON.stringify({
          uuid: result.id,
          originalTitle: result.title,
          aiSummary: result.summary,
          aiHtmlDescription: result.metadata?.aiHtmlDescription,
          aiCategorization: result.category,
          image: result.image,
          source: result.source,
          timeToRead: result.metadata?.timeToRead,
        })
      );
    } catch {
      // best effort
    }
    navigate(`/article/${result.id}`);
  };

  return (
    <section className="container py-8">
      <SectionHeading
        kicker="Search"
        title={searchQuery ? `“${searchQuery}”` : "Search Pembury"}
        standfirst="Matches meaning, not just words. Opening a result teaches the engine."
        debugNote={`/v1/search · filter type!=comment · opening posts event ${NEURON_EVENTS.searchResultOpened} (value 60) with the query`}
        action={
          results.length ? (
            <span className="font-sans text-meta tabular-nums text-ink-muted">
              {results.length} results
            </span>
          ) : null
        }
      />

      {!searchQuery.trim() ? (
        <p className="py-12 text-center text-body text-ink-tertiary">
          Enter a query to search the archive.
        </p>
      ) : loading ? (
        <ul className="divide-y divide-rule border-t border-rule">
          {[0, 1, 2, 3, 4].map((i) => (
            <li key={i} className="flex gap-5 py-5">
              <div className="flex flex-1 flex-col gap-2">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-full" />
              </div>
              <Skeleton className="h-24 w-36 shrink-0" />
            </li>
          ))}
        </ul>
      ) : error ? (
        <p className="py-12 text-center text-body text-ink-tertiary">
          Search is unavailable right now. Please try again.
        </p>
      ) : results.length === 0 ? (
        <p className="py-12 text-center text-body text-ink-tertiary">
          Nothing in the archive matches “{searchQuery}”.
        </p>
      ) : (
        <ul className="divide-y divide-rule border-t border-rule">
          {results.map((result) => (
            <li key={result.id}>
              <article
                role="link"
                tabIndex={0}
                onClick={() => openResult(result)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openResult(result);
                  }
                }}
                className="group flex cursor-pointer gap-5 py-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
              >
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  {result.category ? (
                    <Badge variant="kicker">{result.category}</Badge>
                  ) : null}
                  <h3 className="font-serif text-h3 font-bold leading-tight text-ink decoration-masthead/40 underline-offset-[3px] group-hover:underline">
                    {result.title}
                  </h3>
                  {result.summary ? (
                    <p className="text-body text-ink-secondary line-clamp-2">
                      {result.summary}
                    </p>
                  ) : null}
                  <div className="mt-0.5 flex items-center gap-2">
                    {result.source ? (
                      <span className="font-sans text-meta text-ink-muted">
                        {result.source}
                      </span>
                    ) : null}
                    {result.score != null ? (
                      <span className="font-sans text-meta font-semibold tabular-nums text-pencil">
                        {(result.score * 100).toFixed(0)}% match
                      </span>
                    ) : null}
                  </div>
                </div>

                {result.image ? (
                  <div className="photo-inset h-24 w-36 shrink-0 overflow-hidden rounded-sm bg-surface-2">
                    <img
                      src={result.image}
                      alt=""
                      loading="lazy"
                      className="size-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]"
                    />
                  </div>
                ) : null}
              </article>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default SearchPage;
